import useNavigate from '@/hooks/router/useNavigate';
import { faExclamationTriangle, faTimesCircle } from '@fortawesome/pro-light-svg-icons';
import { alpha, ButtonBase, ButtonBaseProps, Grid, Paper, styled, Typography, Box } from '@mui/material';
import { AuthenticatedUserContext, BasicAlert, useApplicationLinks, useAuthenticatedUser } from '@ngt/openiddict';
import { FC, useContext, useEffect } from 'react';

const StyledButton = styled(ButtonBase)(({ theme }) => ({
    padding: theme.spacing(3),
    cursor: 'pointer',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'left',
    alignItems: 'flex-start',
    background: theme.palette.background.paper,
    backgroundImage: theme.palette.mode === 'dark' ? 
        `linear-gradient(${alpha(theme.palette.common.white, 0.05)}, ${alpha(theme.palette.common.white, 0.05)})` :
        undefined,

    boxShadow: theme.shadows[1],
    borderRadius: theme.shape.borderRadius,

    transition: 'background 1s ease',

    '&:hover': {
        background: theme.palette.mode === 'dark' ? 
        theme.palette.primary.main :
        alpha(theme.palette.primary.main, 0.15),

        '& > *:nth-child(3)': {
            color: theme.palette.grey[400]
        }
    }
}));

const StyledTitle = styled(Typography)(({theme}) => ({
    marginBottom: theme.spacing(2)
}));

const StyledDescription = styled(Typography)(({theme}) => ({
    marginBottom: theme.spacing(2),
    flex: '1 1 auto'
}));

const StyledUrl = styled(Typography)(({theme}) => ({
    color: theme.palette.grey[600]
}));

const Home: FC = () => {
    const { data, loading, error } = useApplicationLinks();
    const {isAdmin, hasAuth, loading: authLoading} = useContext(AuthenticatedUserContext);
    const navigate = useNavigate();

    useEffect(() => {
        if (!isAdmin && !authLoading) {
            navigate(import.meta.env.VITE_API_WEBSITE_URL);
        }
    }, [isAdmin, hasAuth, authLoading, navigate])
    

    return (
        <>
            <Typography variant="h1" gutterBottom>
                Home
            </Typography>
            <Grid container spacing={3}>
                {
                    !loading && error && (
                        <Box
                            sx={{
                                mt: 2
                            }}
                        >
                            <BasicAlert
                                title="Error"
                                description="An error occurred and the list of applications could not be loaded. Please refresh the page and try again."
                                responseStatus={error}
                                icon={faTimesCircle}
                                severity="error"
                            />
                        </Box>
                    )
                }
                {
                    !loading && (!data || data?.length === 0) && (
                        <Box
                            sx={{
                                mt: 2
                            }}
                        >
                            <BasicAlert
                                title="No Applications"
                                description="There are currently no applications currently connected to the Login Server."
                                responseStatus={error}
                                icon={faExclamationTriangle}
                                severity="warning"
                            />
                        </Box>
                    )
                }
                {
                    data && data.length > 0 && data!.map(d => (
                        <Grid 
                            item 
                            xs={12} 
                            sm={12} 
                            md={6} 
                            lg={4} 
                            xl={3}
                            key={d.clientId}
                        >
                            <StyledButton
                                // @ts-ignore-next-line
                                component="a"
                                href={d.homeUri}
                                color="inherit"
                            >
                                <StyledTitle
                                    variant="h4"
                                >
                                    {d.displayName}
                                </StyledTitle>
                                <StyledDescription
                                    variant="body2"
                                >
                                    {d.description}
                                </StyledDescription>
                                <StyledUrl
                                    variant="caption"
                                >
                                    {d.homeUri}
                                </StyledUrl>
                            </StyledButton>
                        </Grid>
                    ))
                }
            </Grid>
        </>
    );
}

export default Home;